import { GetStaticPaths, GetStaticProps } from "next";
import parlors from "goi_common/src/constants/parlors_detail.json";
import { parlorRegion } from "@/constants/parlorRegion";
import {
  DTypeCompareParlor,
  FUNERAL_SIDO,
  ParlorDetailProps,
} from "goi_common";
import { Stack } from "@mui/material";
import Head from "next/head";

const { regionColor, targetSidos } = parlorRegion;

function getByValue(map: Map<number, any>, searchValue: any) {
  for (const [key, value] of map.entries()) {
    if (key === searchValue) return value;
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const parlorList = parlors.filter((res) => targetSidos.includes(res.sido));

  let pathList = [] as Array<any>;
  let copyParlorList = [...parlorList];

  while (copyParlorList.length > 0) {
    let firstParlor = copyParlorList[0];
    copyParlorList.map((secondParlor) => {
      if (
        firstParlor.companyname !== secondParlor.companyname &&
        firstParlor.sigungu === secondParlor.sigungu
      ) {
        pathList.push({
          params: {
            firstplace: `${getByValue(
              FUNERAL_SIDO,
              Number(firstParlor.sido)
            )} 의 ${String(firstParlor.companyname)} 과`,
            secondplace: `${getByValue(
              FUNERAL_SIDO,
              Number(secondParlor.sido)
            )} 의 ${String(secondParlor.companyname)} 비교`,
          },
        });
      }
    });
    copyParlorList.shift();
  }
  return {
    paths: pathList,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const firstparam = params?.firstplace as string;
  const secondparam = params?.secondplace as string;

  const firstplace = firstparam.replace(" 과", "").split(" 의 ")[1];
  const secondplace = secondparam.replace(" 비교", "").split(" 의 ")[1];

  const firstParlor = parlors.filter(
    (p) => String(p.companyname) === String(firstplace)
  )[0];
  const secondParlor = parlors.filter(
    (p) => String(p.companyname) === String(secondplace)
  )[0];

  let description = ` ${firstParlor.companyname}은 ${firstParlor.address},
   ${secondParlor.companyname}은 ${secondParlor.address}에 위치해있습니다. 비교를 토대로 두 장례식장의 상세한 내용을 알아보겠습니다.`;

  return {
    props: {
      firstParlor,
      secondParlor,
      pageDescription: description,
      pageKeywords: `${firstParlor.companyname} 장례 서비스, ${firstParlor.companyname} 얼마, ${firstParlor.companyname} 앰뷸런스, ${firstParlor.companyname} 수의, ${firstParlor.companyname} 유골함, ${firstParlor.companyname} 도우미, ${firstParlor.companyname} 비용, ${firstParlor.companyname} 할인, ${firstParlor.companyname} 전화번호,
      ${secondParlor.companyname} 장례 서비스, ${secondParlor.companyname} 얼마, ${secondParlor.companyname} 앰뷸런스, ${secondParlor.companyname} 수의, ${secondParlor.companyname} 유골함, ${secondParlor.companyname} 도우미, ${secondParlor.companyname} 비용, ${secondParlor.companyname} 할인, ${secondParlor.companyname} 전화번호`,
      pageOgTitle: `${firstParlor.companyname}, ${secondParlor.companyname} 비교 ::  충청대전세종장례소개포털`,
      pageOgDescription: description,
      pageOgImage: firstParlor?.images[0]?.url || "",
    },
  };
};

const sangjoFAQData = [
  {
    question: "장례 비용/음식 비용을 최대한 줄일 수 있나요?",
    answer:
      "고이장례연구소(1666-9784)에서는 미사용 품목은 100% 공제해드리며, 품목별 가격 정찰제를 시행중입니다. 장례식장 비용 비교를 통하여 500만원이상 비용을 줄이며, 합리적으로 장례를 치를 수 있게 상담해드립니다. 특히, 음식 비용의 경우 2~300만원의 차이가 나기도 하는데 고이에서 파견한 장례도우미를 통하여 합리적으로 장례를 치를 수 있게 도와드립니다. <a href='https://www.goifuneral.co.kr/match/request/'>전체 비용 알아보기</a>",
  },
  {
    question: "장례지도사가 빈소에 함께하며 장지까지 동행하나요?",
    answer:
      "장례식장에 연락하여 장례를 진행하는 경우 장례 중 빈소를 잠깐 관리하는 역할을 합니다. 고이의 장례지도사는 장례식장부터 장지, 화장장 예약 등 장례 전반을 책임지며 합리적인 장례를 도와드립니다. 임종 순간부터 장지까지 동행하는 장례지도사, 미리 알아보세요. 고이장례연구소(1666-9784) <a href='https://www.goifuneral.co.kr/review/'>장례지도사 후기 보기</a>",
  },
  {
    question: "빈소를 미리 예약할 수 있나요?",
    answer:
      "아니요, 빈소는 임종 시점에 이용 가능 여부를 확인해야합니다. 특히 지나치게 큰 빈소를 이용하지 않도록 상담해드리고 염두에 두고 있는 빈소의 비용이나, 주변 장례식장 비용 비교까지 도와드립니다.",
  },
];
const faqList = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity:
    sangjoFAQData.map((item) => {
      return {
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      };
    }) || [],
};

export default function placeComaprePage({
  firstParlor,
  secondParlor,
  pageOgTitle,
}: {
  firstParlor: ParlorDetailProps;
  secondParlor: ParlorDetailProps;
  pageOgTitle: string;
}) {
  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqList) }}
        />
      </Head>
      <Stack>
        <DTypeCompareParlor
          firstParlor={firstParlor}
          secondParlor={secondParlor}
          mainColor={regionColor}
          event={{
            domain: "ch",
            path: "chk_compare",
            trigger_and_target: "uv_prlr_detail",
          }}
        />
      </Stack>
    </>
  );
}
